import { ExtendedUserInfo, SimpleUserInfo } from './forms/UserInfoTab';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import AppBar from '@material-ui/core/AppBar';
import DefaultDialog from 'ui/dialog/DefaultDialog';
import DevicesTab from './forms/DevicesTab';
import DialogActions from '@material-ui/core/DialogActions';
import DialogButtons from 'ui/dialog/Buttons';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DualListBox from 'ui/duallist/DualListBox';
import React from 'react';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import formatters from '../../../formatters';
import { isValidEmail, notEmpty, maxLength } from '../../../utils/validators';
import { useState } from 'react';

const userFormStyles = theme => ({
    appBar: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.secondary.main,
        paddingBottom: 10,
    },
    appBarRoot: {
        position: 'relative',
        '&:after': {
            content: '""',
            borderBottom: `3px solid ${theme.palette.grey['300']}`,
            position: 'absolute',
            bottom: 18,
            left: -24,
            right: -24,
            zIndex: -1,
        },
    },
    appBarTab: {
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    stat: {
        paddingRight: 0,
        marginLeft: theme.spacing(10),
        fontWeight: 500,
    },
    dialogToolbar: { padding: 0 },
    dialogToolbarTabs: { flex: 1 },
    list: { height: 450 },
});

const useDialogStyles = makeStyles({
    dialogFixed: { height: 750 },
});

const MAX_NAME_LENGTH = 50;
const MAX_EMAIL_LENGTH = 150;

const runValidator = (validator, values) => {
    const result = validator(values);
    return result ? result[1] : '';
};

const validateEmail = email => {
    const trimmedEmail = (email || '').trim();
    if (!trimmedEmail) {
        return 'Required';
    }
    if (trimmedEmail.length > MAX_EMAIL_LENGTH) {
        return `Invalid: value must not exceed ${MAX_EMAIL_LENGTH} characters`;
    }
    if (!isValidEmail(trimmedEmail)) {
        return 'Invalid email address';
    }
    return '';
};

const validate = userInfo => {
    const errors = {
        first_name:
            runValidator(notEmpty('first_name'), userInfo) ||
            runValidator(maxLength('first_name', MAX_NAME_LENGTH), userInfo),
        last_name:
            runValidator(notEmpty('last_name'), userInfo) ||
            runValidator(maxLength('last_name', MAX_NAME_LENGTH), userInfo),
        email: validateEmail(userInfo.email),
    };
    const valid = Object.values(errors).every(val => !val);
    return [valid, errors];
};

const renderUserInfo = (
    currentUser,
    userInfo,
    userInfoErrors,
    setUserInfo,
    managedByPb,
    mode,
    canEditAdmin,
    extraAction,
    hideAdmin,
    loggedInUser
) => {
    if (mode === 'new' || mode === 'details') {
        return (
            <SimpleUserInfo
                userInfo={userInfo}
                mode={mode}
                userInfoErrors={userInfoErrors}
                onChange={(prop, val) => {
                    setUserInfo({ ...userInfo, [prop]: val });
                }}
                hideAdmin={hideAdmin}
            />
        );
    }

    return (
        <ExtendedUserInfo
            mode={mode}
            managedByPb={managedByPb}
            userInfo={userInfo}
            currentUser={currentUser}
            userInfoErrors={userInfoErrors}
            canEditAdmin={canEditAdmin}
            onChange={(prop, val) => {
                setUserInfo({ ...userInfo, [prop]: val });
            }}
            extraAction={extraAction}
            hideAdmin={hideAdmin}
            loggedInUser={loggedInUser}
        />
    );
};

const getInitialUserState = (currentUser, hideAdmin) => {
    let userState = {
        first_name: '',
        last_name: '',
        email: '',
    };
    if (currentUser) {
        userState = {
            uuid: currentUser.uuid,
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            email: currentUser.email,
            master_password_change_required:
                currentUser.master_password_change_required,
            disabled: !currentUser.enabled,
        };
    }
    if (!hideAdmin) {
        userState = {
            ...userState,
            admin: currentUser ? currentUser.role === 'Administrator' : false,
        };
    }
    return userState;
};

const getInitialGroups = (currentUser, groups) => {
    if (currentUser && currentUser.groups) {
        return currentUser.groups.map(({ uuid }) => uuid);
    }
    const everyoneGroup = groups.find(g => g.name === 'Everyone');
    return everyoneGroup ? [everyoneGroup.uuid] : [];
};

const getInitialDevices = currentUser => {
    if (currentUser) {
        return currentUser.devices;
    }
    return [];
};

const leftAction = (onDelete, managedByPb, currentUser) => {
    if (currentUser) {
        if (onDelete && managedByPb) {
            return (
                <DialogButtons.Secondary onClick={onDelete} label="Delete" />
            );
        } else if (!managedByPb && currentUser.managed_by) {
            return (
                <Typography style={{ paddingLeft: 15 }} color="secondary">
                    Managed By:{' '}
                    {formatters.managedByLabel(currentUser.managed_by)}
                </Typography>
            );
        }
    }
};

const isGroupSelectable = (group, user) => {
    if (group.population_mode === 'automatic') {
        return false;
    }

    if (user && user.managed_by === 'PB') {
        return true;
    } else {
        return group.managed_by === 'PB';
    }
};

const getGroupsOptions = (groups, currentUser) => {
    if (groups && groups.length) {
        return groups
            .filter(group => group.status !== 'pending_delete')
            .map(group => ({
                value: group.uuid,
                selectable: isGroupSelectable(group, currentUser),
                label: formatters.displayName(group.name, group.managed_by),
            }));
    }
    if (currentUser && currentUser.groups) {
        return currentUser.groups.map(group => ({
            value: group.uuid,
            label: group.name,
        }));
    }
    return [];
};

let UserForm = props => {
    const {
        classes,
        open,
        onOk,
        onCancel,
        cancelButtonName = 'Cancel',
        okButtonName = 'Add User',
        onDelete,
        availableLicenses,
        formErrors,
        mode,
        currentUser,
        canEditAdmin = true,
        extraAction,
        tabs,
        hideAdmin,
        loggedInUser,
    } = props;

    const managedByPb = currentUser ? currentUser.managed_by === 'PB' : true;

    const title = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`
        : 'Add User';

    const groupsOptions = getGroupsOptions(props.groups, currentUser);

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState(
        getInitialGroups(currentUser, props.groups)
    );

    const [devices, setDevices] = useState(getInitialDevices(currentUser));
    const [devicesToRemove, setDevicesToRemove] = useState([]);

    const [userInfo, setUserInfo] = useState(
        getInitialUserState(currentUser, hideAdmin)
    );

    const [userInfoErrors, setUserInfoErrors] = useState({
        first_name: '',
        last_name: '',
        email: '',
    });
    const onOkButton = async () => {
        if (mode === 'details') {
            onOk();
        } else {
            const [valid, errors] = validate(userInfo);
            setUserInfoErrors(errors);
            if (valid) {
                if (!hideAdmin) {
                    userInfo.organization_account =
                        currentUser && currentUser.organization_account_uuid;
                    userInfo.groups = groups;
                    userInfo.devices_to_remove = devicesToRemove;
                }
                setLoading(true);
                await onOk(userInfo);
                setLoading(false);
            }
        }
    };

    const dialogClasses = useDialogStyles();

    return (
        <DefaultDialog
            classes={{ dialogFixed: dialogClasses.dialogFixed }}
            open={open}
            onClose={onCancel}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <AppBar
                    position="static"
                    className={classes.appBar}
                    classes={{
                        root: classes.appBarRoot,
                    }}
                    elevation={0}
                >
                    <Toolbar className={classes.dialogToolbar}>
                        <Tabs
                            value={tab}
                            onChange={(event, val) => setTab(val)}
                            textColor="secondary"
                            className={classes.dialogToolbarTabs}
                        >
                            {tabs.map(tab => {
                                const show = !tab.mode || mode === tab.mode;
                                return show ? (
                                    <Tab
                                        classes={{ root: classes.appBarTab }}
                                        label={tab.label}
                                    />
                                ) : null;
                            })}
                        </Tabs>
                        <Typography className={classes.stat}>
                            Available Licenses: {availableLicenses}
                        </Typography>
                    </Toolbar>
                </AppBar>
                {tab === 0 && (
                    <>
                        {renderUserInfo(
                            currentUser,
                            userInfo,
                            userInfoErrors,
                            setUserInfo,
                            managedByPb,
                            mode,
                            canEditAdmin,
                            extraAction,
                            hideAdmin,
                            loggedInUser
                        )}
                        {formErrors && (
                            <Typography color="error">{formErrors}</Typography>
                        )}
                    </>
                )}
                {tab === 1 && (
                    <>
                        <DualListBox
                            disabled={mode === 'details'}
                            options={groupsOptions}
                            initialValue={groups}
                            sourceListHeaderName="Available Groups"
                            destinationListHeaderName="Member of"
                            onChange={values => setGroups(values)}
                            className={classes.list}
                        />
                        {!managedByPb && (
                            <Typography>
                                Add or remove groups managed by CyberFOX
                                Password Manager. Groups managed by external
                                connectors are added or removed in source
                                directory.
                            </Typography>
                        )}
                    </>
                )}
                {tab === 2 && (
                    <DevicesTab
                        mode={mode}
                        devices={devices}
                        onChange={uuids => {
                            const uuidsSet = new Set(uuids);
                            const newDevices = devices.filter(
                                d => !uuidsSet.has(d.uuid)
                            );
                            setDevices(newDevices);
                            setDevicesToRemove([...devicesToRemove, ...uuids]);
                        }}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <DialogButtons withLeft={tab === 0}>
                    {tab === 0 && (
                        <div>
                            {leftAction(onDelete, managedByPb, currentUser)}
                        </div>
                    )}
                    <div>
                        {onCancel && (
                            <DialogButtons.Secondary
                                label={cancelButtonName}
                                onClick={onCancel}
                                disabled={loading}
                            />
                        )}
                        <DialogButtons.Primary
                            label={okButtonName}
                            onClick={onOkButton}
                            variant="contained"
                            autoFocus
                            loading={loading}
                        />
                    </div>
                </DialogButtons>
            </DialogActions>
        </DefaultDialog>
    );
};

export default withStyles(userFormStyles)(UserForm);
